import { Location } from '@angular/common';
import type { HttpErrorResponse } from '@angular/common/http';
import { ApplicationInitStatus, computed, effect, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  AUTH_PUBLIC_PATHS,
  AUTH_STRATEGY,
  isAuthGuestOnlyPath,
} from '@indutrust/auth-model';
import { CompanyApiClient } from '@indutrust/company-data';
import type {
  Company,
  RequestStatus,
  TeamRole,
  User,
} from '@indutrust/shared-model';
import { resolveUserTeamRole } from '@indutrust/shared-model';
import { resolveHttpError } from '@indutrust/shared-util';
import { mapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import {
  Events,
  injectDispatch,
  on,
  withEventHandlers,
  withReducer,
} from '@ngrx/signals/events';
import { switchMap } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { navigateOrReport } from '../../util/navigate-or-report.util';
import { loginEvents } from '../login';
import { registerEvents } from '../register';
import { resetPasswordEvents } from '../reset-password';
import { authSessionEvents } from './auth-session.events';

type AuthSessionState = {
  authenticationInitialized: boolean;

  // OAuth2 tokens
  accessToken: string | null;
  refreshToken: string | null;

  // User session
  user: User | null;
  userRequestState: RequestStatus;

  // Company
  company: Company | null;
  companyRequestState: RequestStatus;
};

/**
 * Signal store for managing authenticated user session state.
 *
 * Handles core authentication state including tokens, user data, and company information.
 * This is the source of truth for the authenticated user's session.
 *
 * Other stores (Login, Register, PasswordReset) dispatch events that ultimately
 * update this store when authentication operations succeed.
 *
 * @example
 * const session = inject(AuthSessionStore);
 * if (session.isAuthenticated()) {
 *   console.log('User:', session.user());
 * }
 * if (session.isPaid()) {
 *   // paid licence — also read by {@link paidGuard}
 * }
 * if (session.isTeamViewer()) {
 *   // read-only on company/factory edit and other mutation surfaces (R5)
 * }
 * if (session.isTeamManager()) {
 *   // Owner or Manager — may mutate supplier content (R5)
 * }
 */
export const AuthSessionStore = signalStore(
  { providedIn: 'root' },
  withState<AuthSessionState>({
    authenticationInitialized: false,

    accessToken:
      typeof localStorage === 'undefined'
        ? null
        : localStorage.getItem('access_token'),
    refreshToken:
      typeof localStorage === 'undefined'
        ? null
        : localStorage.getItem('refresh_token'),

    userRequestState: 'initial',
    user: null,

    company: null,
    companyRequestState: 'initial',
  }),
  withComputed(
    ({
      accessToken,
      user,
      userRequestState,
      company,
      companyRequestState,
    }) => ({
      isAuthenticated: computed(() => {
        return (accessToken()?.length ?? 0) > 0 && !!user() && !!company();
      }),

      hasClientPermissions: computed(() => {
        const userType = user()?.type;
        return (
          userType === 'admin' ||
          userType === 'government' ||
          userType === 'zone' ||
          userType === 'buyer'
        );
      }),

      isAdmin: computed(() => {
        return user()?.type === 'admin';
      }),

      isSupplier: computed(() => {
        return user()?.type === 'supplier';
      }),

      isClient: computed(() => {
        const userType = user()?.type;
        return (
          userType === 'government' ||
          userType === 'zone' ||
          userType === 'buyer'
        );
      }),

      isGovernment: computed(() => {
        return user()?.type === 'government';
      }),

      isZone: computed(() => {
        return user()?.type === 'zone';
      }),

      isBuyer: computed(() => {
        return user()?.type === 'buyer';
      }),

      isPaid: computed(() => {
        return company()?.status === 'paid';
      }),

      isTeamManager: computed(() => {
        const role = resolveUserTeamRole(user());
        return role === 'owner' || role === 'manager';
      }),

      isTeamOwner: computed(() => {
        return resolveUserTeamRole(user()) === 'owner';
      }),

      isTeamViewer: computed(() => {
        return resolveUserTeamRole(user()) === 'viewer';
      }),

      teamRole: computed((): TeamRole => resolveUserTeamRole(user())),

      userError: computed(() => {
        const status = userRequestState();
        return typeof status === 'object' ? status.error : null;
      }),

      companyError: computed(() => {
        const status = companyRequestState();
        return typeof status === 'object' ? status.error : null;
      }),
    })
  ),
  withMethods((store) => ({
    setAccessToken(token: string): void {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('access_token', token);
      }
      patchState(store, { accessToken: token });
    },
  })),
  withReducer(
    on(
      authSessionEvents.userCompanyFailed,
      authSessionEvents.userCompanySucceeded,
      authSessionEvents.logoutCompleted,
      () => ({
        authenticationInitialized: true,
      })
    ),

    on(
      loginEvents.loginRequested,
      registerEvents.registerRequested,
      resetPasswordEvents.resetPasswordRequested,
      () => ({
        companyRequestState: 'initial',
        userRequestState: 'initial',
      })
    ),

    on(authSessionEvents.userDataRequested, () => ({
      userRequestState: 'loading',
    })),
    on(authSessionEvents.userDataSucceeded, ({ payload }) => ({
      user: payload,
      userRequestState: 'success',
    })),
    on(authSessionEvents.userDataFailed, ({ payload }) => ({
      accessToken: null,
      company: null,
      companyRequestState: 'initial',
      refreshToken: null,
      user: null,
      userRequestState: { error: payload.error },
    })),

    on(authSessionEvents.userCompanyRequested, () => ({
      companyRequestState: 'loading',
    })),
    on(authSessionEvents.userCompanySucceeded, ({ payload }) => ({
      company: payload,
      companyRequestState: 'success',
    })),
    on(authSessionEvents.userCompanyFailed, ({ payload }) => ({
      accessToken: null,
      company: null,
      companyRequestState: payload,
      refreshToken: null,
      user: null,
      userRequestState: 'initial',
    })),

    on(authSessionEvents.logoutRequested, () => ({
      authenticationInitialized: true,
      accessToken: null,
      refreshToken: null,
      userRequestState: 'initial',
      user: null,
      company: null,
      companyRequestState: 'initial',
    }))
  ),
  withEventHandlers(
    (
      store,
      events = inject(Events),
      authService = inject(AUTH_STRATEGY),
      companyService = inject(CompanyApiClient),
      publicPaths = inject(AUTH_PUBLIC_PATHS),
      router = inject(Router),
      location = inject(Location)
    ) => {
      return {
        initializeAuth$: events
          .on(authSessionEvents.authenticationInitialized)
          .pipe(
            map(() => {
              if (store.accessToken()) {
                return authSessionEvents.userDataRequested();
              }
              return authSessionEvents.logoutRequested();
            })
          ),

        loadUserData$: events.on(authSessionEvents.userDataRequested).pipe(
          switchMap(() =>
            authService.getConnectedUser().pipe(
              mapResponse({
                next: (user: User) => authSessionEvents.userDataSucceeded(user),
                error: (error: HttpErrorResponse) => {
                  console.error(error);
                  return authSessionEvents.userDataFailed({
                    error: resolveHttpError(error).code,
                  });
                },
              })
            )
          )
        ),

        loadUserCompany$: events
          .on(
            authSessionEvents.userCompanyRequested,
            authSessionEvents.userDataSucceeded
          )
          .pipe(
            map(() => store.user()?.company_id),
            filter((companyId) => companyId !== undefined),
            switchMap((companyId) =>
              companyService.getById(companyId).pipe(
                mapResponse({
                  next: (company) =>
                    authSessionEvents.userCompanySucceeded(company),
                  error: (error: HttpErrorResponse) => {
                    console.error(error);
                    return authSessionEvents.userCompanyFailed({
                      error: resolveHttpError(error).code,
                    });
                  },
                })
              )
            )
          ),

        logout$: events
          .on(
            authSessionEvents.logoutRequested,
            authSessionEvents.userCompanyFailed,
            authSessionEvents.userDataFailed
          )
          .pipe(
            map(() => {
              if (typeof localStorage !== 'undefined') {
                localStorage.removeItem('access_token');
                localStorage.removeItem('refresh_token');
              }
              if (
                !publicPaths.some((path) => location.path().startsWith(path))
              ) {
                void router.navigate(['/login']);
              }
              return authSessionEvents.logoutCompleted();
            })
          ),

        setTokensAndGetUserAfterLogin$: events
          .on(
            loginEvents.loginSucceeded,
            registerEvents.registerSucceeded,
            resetPasswordEvents.resetPasswordSucceeded
          )
          .pipe(
            map(({ payload }) => {
              store.setAccessToken(payload.accessToken);
              return authSessionEvents.userDataRequested();
            })
          ),
      };
    }
  ),
  withHooks((store, location = inject(Location), router = inject(Router)) => {
    const dispatch = injectDispatch(authSessionEvents);

    return {
      onInit: () => {
        const initStatus = inject(ApplicationInitStatus);
        void initStatus.donePromise.then(() => {
          dispatch.authenticationInitialized();
        });

        effect(() => {
          if (store.isAuthenticated() && isAuthGuestOnlyPath(location.path())) {
            navigateOrReport(router, ['/'], 'Navigation to Home failed');
          }
        });
      },
    };
  })
);
